import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { RequireAuth } from "./components/RequireAuth";
import { SimulationSelectPage } from "./pages/SimulationSelectPage";
import { IntroPage } from "./pages/IntroPage";
import { AdminBuilderPage } from "./pages/AdminBuilderPage";
import { AdminSimLibraryPage } from "./pages/AdminSimLibraryPage";
import { AdminSimEditPage } from "./pages/AdminSimEditPage";
import { AdminClientsPage } from "./pages/AdminClientsPage";
import { AdminUsersPage } from "./pages/AdminUsersPage";
import { AdminAnalyticsPage } from "./pages/AdminAnalyticsPage";
import { AdminAuditPage } from "./pages/AdminAuditPage";
import { MyDataPage } from "./pages/MyDataPage";
import { DashboardPage } from "./pages/DashboardPage";
import { AdminCohortsPage } from "./pages/AdminCohortsPage";
import { AttemptPlayerPage } from "./pages/AttemptPlayerPage";
import { ReplayPage } from "./pages/ReplayPage";
import { ResultsPage } from "./pages/ResultsPage";
import { ReflectionPage } from "./pages/ReflectionPage";
import { DecisionDemoPage } from "./pages/DecisionDemoPage";
import { NotFoundPage } from "./pages/NotFoundPage";

// Coaching pulls in the (heavy) PDF renderer — load it only when reached.
const CoachingPage = lazy(() =>
  import("./pages/CoachingPage").then((m) => ({ default: m.CoachingPage })),
);

function RouteFallback(): JSX.Element {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-neusim-bg">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-fuchsia-400" />
    </div>
  );
}

export function App(): JSX.Element {
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/" element={<Navigate to="/simulations" replace />} />
      <Route
        path="/simulations"
        element={
          <RequireAuth>
            <SimulationSelectPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/sims/new"
        element={
          <RequireAuth>
            <AdminBuilderPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/sims"
        element={
          <RequireAuth>
            <AdminSimLibraryPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/sims/:simId"
        element={
          <RequireAuth>
            <AdminSimEditPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <RequireAuth>
            <AdminClientsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/users"
        element={
          <RequireAuth>
            <AdminUsersPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <RequireAuth>
            <AdminAnalyticsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/audit"
        element={
          <RequireAuth>
            <AdminAuditPage />
          </RequireAuth>
        }
      />
      <Route
        path="/my/data"
        element={
          <RequireAuth>
            <MyDataPage />
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard"
        element={
          <RequireAuth>
            <DashboardPage />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/cohorts"
        element={
          <RequireAuth>
            <AdminCohortsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/sim/:simId/intro"
        element={
          <RequireAuth>
            <IntroPage />
          </RequireAuth>
        }
      />
      <Route
        path="/sim/:simId/attempt/:attemptId/play"
        element={
          <RequireAuth>
            <AttemptPlayerPage />
          </RequireAuth>
        }
      />
      <Route
        path="/sim/:simId/replay"
        element={
          <RequireAuth>
            <ReplayPage />
          </RequireAuth>
        }
      />
      <Route
        path="/sim/:simId/attempt/:attemptId/results"
        element={
          <RequireAuth>
            <ResultsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/sim/:simId/attempt/:attemptId/reflection"
        element={
          <RequireAuth>
            <ReflectionPage />
          </RequireAuth>
        }
      />
      <Route
        path="/sim/:simId/attempt/:attemptId/coaching"
        element={
          <RequireAuth>
            <CoachingPage />
          </RequireAuth>
        }
      />
      {/* Design reference — the original chrome demo, kept accessible during Phase 2 */}
      <Route path="/demo" element={<DecisionDemoPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}
